import React, { useState } from "react";
import { Router, Route, match, Switch } from "react-router-dom";
import { createBrowserHistory, History } from "history";

import { NotificationsContainer, NotFoundPage } from "fe-shared/src/components";
import useExternalAxiosConfig from "fe-shared/src/hooks/useExternalAxiosConfig";
import { Company, User, UserInfo } from "fe-shared/src/types/domain";

import { EXTERNAL_CLIENT_ID } from "fe-shared/src/consts";

import UserContext from "contexts/userContext";
import TransfersGrid from "routes/Transfers";
import TransferDetails from "routes/TransferDetails";
import TransactionsList from "routes/TransactionsList";
import TransactionDetails from "routes/TransactionDetails";
import InvoicesDetails from "routes/InvoicesDetails";
import InvoicesList from "routes/Invoices";

enum Feature {
  Transactions = "Transactions",
  Transfers = "Transfers",
  Invoices = "Invoices",
}

const defaultProps = {
  setUserData: (data: UserInfo | null) => data,
};

interface Props {
  history?: History;
  match?: match<{}>;
  apiBaseUrl?: string;
  feature?: Feature;
  user?: {
    token: string;
    data: User;
  };
  company?: Company;
  setUserData?: (data: UserInfo | null) => void;
  refreshTokenUrl?: string;
  refreshTokenBaseUrl?: string;
  forbiddenCallback?: () => void;
  sessionId?: string;
}

const App = (props: Props) => {
  const {
    apiBaseUrl,
    user,
    company,
    setUserData = defaultProps.setUserData,
    feature,
    refreshTokenUrl,
    refreshTokenBaseUrl,
    forbiddenCallback,
    sessionId,
  } = props;

  const matchUrl = props.match?.url || "";
  const history = props.history || createBrowserHistory();
  const [lastOpenedNumber, setLastOpenedNumber] = useState<string | null>(null);
  const [lastOpenedTransfersNumber, setLastOpenedTransfersNumber] = useState<
    string | null
  >(null);
  const [lastOpenedInvoiceNumber, setLastOpenedInvoiceNumber] = useState<
    string | null
  >(null);
  const isReady = useExternalAxiosConfig(
    user,
    setUserData,
    apiBaseUrl,
    refreshTokenUrl,
    company?.companyId,
    forbiddenCallback,
    sessionId,
    EXTERNAL_CLIENT_ID,
    refreshTokenBaseUrl
  );

  if (!isReady) {
    return null;
  }

  return (
    <UserContext.Provider value={user?.data}>
      <Router history={history}>
        <Switch>
          {feature === Feature.Transactions && (
            <>
              <Route path={matchUrl} exact>
                <TransactionsList
                  company={company!}
                  lastOpenedNumber={lastOpenedNumber}
                  clearLastOpenedNumber={() => setLastOpenedNumber(null)}
                />
              </Route>
              <Route path={`${matchUrl}/:transactionNumber`}>
                <TransactionDetails
                  company={company!}
                  setLastOpenedNumber={setLastOpenedNumber}
                  apiBaseUrl={apiBaseUrl}
                />
              </Route>
            </>
          )}
          {feature === Feature.Transfers && (
            <Route path={matchUrl} exact>
              <TransfersGrid
                company={company!}
                lastOpenedTransfersNumber={lastOpenedTransfersNumber}
                clearLastOpenedTransfersNumber={() => {
                  setLastOpenedTransfersNumber(null);
                }}
              />
            </Route>
          )}
          {feature === Feature.Transfers && (
            <Route path={`${matchUrl}/:transferNumber`}>
              <TransferDetails
                company={company!}
                setLastOpenedTransfersNumber={setLastOpenedTransfersNumber}
              />
            </Route>
          )}
          {feature === Feature.Invoices && (
            <Route path={matchUrl} exact>
              <InvoicesList
                company={company!}
                lastOpenedInvoiceNumber={lastOpenedInvoiceNumber}
                clearLastOpenedInvoiceNumber={() => {
                  setLastOpenedInvoiceNumber(null);
                }}
              />
            </Route>
          )}
          {feature === Feature.Invoices && (
            <Route path={`${matchUrl}/:invoiceNumber`}>
              <InvoicesDetails
                company={company!}
                setLastOpenedInvoiceNumber={setLastOpenedInvoiceNumber}
              />
            </Route>
          )}
          <Route path="*">
            <NotFoundPage />
          </Route>
        </Switch>
        <NotificationsContainer />
      </Router>
    </UserContext.Provider>
  );
};

export default App;
